// src/ui/App.tsx
import { h, VNode } from "preact";
import { useState, useEffect } from "preact/hooks";
import { lazy, Suspense } from "preact/compat";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LoadingSpinner } from "./components/LoadingSpinner";
import type { AuthStatus } from "../types";

// Import Auth component directly for initial render
import Auth from "./Auth";

// Lazy load Chat component
const Chat = lazy(() =>
    import("./Chat").catch((error) => {
        console.error("Failed to load Chat component:", error);
        throw error;
    }),
);

interface NavigationProps {
    onLogout: () => Promise<void>;
}

const Navigation = ({ onLogout }: NavigationProps): VNode => {
    return (
        <nav
            role="navigation"
            aria-label="Main navigation"
            className="main-navigation"
        >
            <button
                onClick={onLogout}
                aria-label="Log out"
                className="nav-button logout-button"
            >
                Logout
            </button>
        </nav>
    );
};

function MainContent(): VNode {
    const { authStatus, logout } = useAuth();
    const [error, setError] = useState<Error | null>(null);
    const [isClient, setIsClient] = useState(false);

    // Check if we're on the client
    useEffect(() => {
        setIsClient(true);
    }, []);

    const handleLogout = async (): Promise<void> => {
        try {
            setError(null);
            await logout();
        } catch (err) {
            const error =
                err instanceof Error
                    ? err
                    : new Error("Unknown error during logout");
            setError(error);
            console.error("Logout failed:", error);
        }
    };

    const renderContent = (): VNode => {
        // On server or initial client load, show Auth
        if (!isClient) {
            return <Auth />;
        }

        // Once on client, handle based on auth status
        switch (authStatus) {
            case "loading":
                return <LoadingSpinner />;
            case "authenticated":
                return (
                    <Suspense fallback={<LoadingSpinner />}>
                        <Chat />
                    </Suspense>
                );
            case "unauthenticated":
            default:
                return <Auth />;
        }
    };

    return (
        <div role="main" className="app-container">
            <header role="banner" className="app-header">
                <h1>Flexible AI Agent Application</h1>
                {authStatus === "authenticated" && (
                    <Navigation onLogout={handleLogout} />
                )}
                {error && (
                    <div role="alert" className="error-message">
                        {error.message}
                    </div>
                )}
            </header>
            <ErrorBoundary>{renderContent()}</ErrorBoundary>
        </div>
    );
}

export default function App(): VNode {
    return (
        <ErrorBoundary>
            <AuthProvider>
                <MainContent />
            </AuthProvider>
        </ErrorBoundary>
    );
}
